import { callableName } from "./callableRouting";
import {
  Auth, AuthCredential, GoogleAuthProvider, User, UserCredential,
  createUserWithEmailAndPassword, EmailAuthProvider, getAuth, getIdToken,
  linkWithCredential, linkWithPopup, sendPasswordResetEmail,
  signInWithCredential, signInWithEmailAndPassword, signInWithPopup,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

interface PreviousAccount {
  uid: string;
  user: User;
  token: Promise<string | null>;
}
interface Completion {
  targetUID: string;
  previous: PreviousAccount | null;
  merged: boolean;
}
interface LoginCallbacks {
  merge: (previousIDToken: string) => Promise<unknown>;
  register: () => Promise<unknown>;
}

export class LoginFlowError extends Error {
  constructor(public code: string) {
    super(code);
    Object.setPrototypeOf(this, LoginFlowError.prototype);
  }
}
const codeOf = (error: unknown): string =>
  typeof (error as any)?.code === "string" ? (error as any).code : "unknown";

/** Credentials and merge tokens stay in memory for the active sign-in only. */
export class LoginFlow {
  private completion: Completion | null = null;
  private providerLink: { credential: AuthCredential; email: string; previous: PreviousAccount | null } | null = null;
  constructor(
    private auth: Auth = getAuth(),
    private callbacks: LoginCallbacks = {
      merge: token => httpsCallable(getFunctions(), callableName("mergeAccounts"))(token),
      register: () => httpsCallable(getFunctions(), callableName("didRegister"))(),
    },
  ) {}
  get needsCompletion(): boolean { return this.completion !== null; }
  get providerLinkEmail(): string | null { return this.providerLink?.email || null; }
  cancelProviderLink(): void { this.providerLink = null; }

  private previousAccount(): PreviousAccount | null {
    const user = this.auth.currentUser;
    return user ? {
      uid: user.uid, user,
      // Start before switching accounts; refresh errors must not become
      // unhandled rejections. The old User can retry if merging needs it.
      token: getIdToken(user, true).catch(() => null),
    } : null;
  }
  private async finish(result: UserCredential, previous: PreviousAccount | null) {
    this.completion = { targetUID: result.user.uid, previous, merged: false };
    await this.retryCompletion();
  }
  async retryCompletion(): Promise<void> {
    const pending = this.completion;
    if (!pending) return;
    if (this.auth.currentUser?.uid !== pending.targetUID) throw new LoginFlowError("qcc/session-changed");
    if (!pending.merged && pending.previous && pending.previous.uid !== pending.targetUID) {
      const token = await pending.previous.token || await getIdToken(pending.previous.user, true);
      await this.callbacks.merge(token);
      pending.merged = true;
    }
    // The server registration helper is idempotent. Success follows merging
    // and required profile bookkeeping, rather than just an Auth-state event.
    await this.callbacks.register();
    this.completion = null;
  }
  async signInEmail(email: string, password: string): Promise<void> {
    const link = this.providerLink;
    if (link && email.toLowerCase() !== link.email.toLowerCase()) throw new LoginFlowError("qcc/provider-email-mismatch");
    const previous = link ? link.previous : this.previousAccount();
    const result = await signInWithEmailAndPassword(this.auth, email, password);
    if (link) {
      await linkWithCredential(result.user, link.credential);
      this.providerLink = null;
    }
    await this.finish(result, previous);
  }
  async createEmail(email: string, password: string): Promise<void> {
    const previous = this.previousAccount();
    let result: UserCredential;
    try {
      result = this.auth.currentUser
        ? await linkWithCredential(this.auth.currentUser, EmailAuthProvider.credential(email, password))
        : await createUserWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      if (!["auth/credential-already-in-use", "auth/email-already-in-use"].includes(codeOf(error))) throw error;
      result = await signInWithEmailAndPassword(this.auth, email, password);
    }
    await this.finish(result, previous);
  }
  async signInGoogle(): Promise<void> {
    const previous = this.previousAccount();
    const provider = new GoogleAuthProvider();
    provider.addScope("https://www.googleapis.com/auth/userinfo.email");
    provider.setCustomParameters({ prompt: "select_account" });
    let result: UserCredential;
    try {
      // No await before opening the popup: retain the browser's user gesture.
      // Like the old patch, link registered users as well as anonymous users.
      const user = this.auth.currentUser;
      result = user && !user.providerData.some(entry => entry.providerId === GoogleAuthProvider.PROVIDER_ID)
        ? await linkWithPopup(user, provider)
        : await signInWithPopup(this.auth, provider);
    } catch (error) {
      const code = codeOf(error);
      const credential = GoogleAuthProvider.credentialFromError(error as FirebaseError);
      if (credential && ["auth/credential-already-in-use", "auth/email-already-in-use", "auth/provider-already-linked"].includes(code)) {
        result = await signInWithCredential(this.auth, credential);
      } else if (code === "auth/account-exists-with-different-credential" && credential) {
        const email = (error as FirebaseError).customData?.email;
        if (typeof email !== "string" || !email) throw new LoginFlowError("qcc/existing-provider-required");
        this.providerLink = { credential, email, previous };
        throw new LoginFlowError("qcc/link-existing-provider");
      } else throw error;
    }
    await this.finish(result, previous);
  }
  async resetPassword(email: string): Promise<void> {
    try { await sendPasswordResetEmail(this.auth, email); }
    catch (error) {
      // Confirmation never reveals whether this address has an account.
      if (codeOf(error) !== "auth/user-not-found") throw error;
    }
  }
}

export function loginErrorMessage(error: unknown): string {
  switch (codeOf(error)) {
    case "auth/invalid-credential": case "auth/invalid-login-credentials":
    case "auth/wrong-password": case "auth/user-not-found":
      return "That email and password didn’t work. Try again or reset your password.";
    case "auth/invalid-email": return "Enter a valid email address.";
    case "auth/weak-password": return "Use a password with at least six characters.";
    case "auth/email-already-in-use": case "auth/provider-already-linked":
      return "Use Sign in for an existing account, or choose a different sign-in method.";
    case "auth/popup-closed-by-user": case "auth/cancelled-popup-request": return "Sign-in was cancelled. You can try again.";
    case "auth/popup-blocked": return "Allow the sign-in popup in your browser, then try again.";
    case "auth/network-request-failed": return "Couldn’t connect. Check your connection and try again.";
    case "auth/too-many-requests": return "Too many attempts. Please wait a little before trying again.";
    case "qcc/link-existing-provider": return "Sign in with your existing password to connect Google to this account.";
    case "qcc/existing-provider-required": return "Sign in with your existing method first, then connect Google.";
    case "qcc/provider-email-mismatch": return "Use the email address from your Google sign-in to connect this account.";
    case "qcc/session-changed": return "Your signed-in account changed. Reload this page before trying again.";
    default: return "Couldn’t finish signing in. Please try again.";
  }
}
